import { FC, createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { clickNavigation, getNavigationLabelList } from '@/api/common';
import { getZipcode } from '@/utils/common/porder';
import { PageName } from '@/constants/track';
import { getOldPageKey } from '@/tracks/getPageKey';
import usePageMountEffect from '@/hooks/usePageMountEffect';

type ContextApis = {
  labelConfigs: Label[];
  update: () => void;
  onClick: (navigationKey: NavigateKey) => void;
};

export const NavigationContext = createContext<ContextApis>({
  labelConfigs: [],
  update: () => {
    return null;
  },
  onClick: (navigationKey: NavigateKey) => {
    return null;
  }
});

export const useNavigationService = () => {
  const apis = useContext(NavigationContext);
  return {
    ...apis
  };
};

export enum NavigateKey {
  HOME = 'home',
  EXPLORE = 'explore',
  RESTAURANT = 'restaurant',
  GLOBAL = 'global',
  COMMUNITY = 'community',
  ME = 'me'
}

export enum LabelType {
  CIRCLE = 'circle', // 圆气泡(有数字)
  BUBBLE = 'bubble', // new 标签
  DOT = 'dot' // 纯红点
}

export type Label = {
  navigate_key: NavigateKey;
  navigate_config_id: number;
  label_type: LabelType;
  label_background_color: string;
  click_appear: boolean;
  label_value: string;
};

const FETCH_CONFIG_PAGES = [PageName.page_home, PageName.page_category, PageName.page_me, PageName.page_comm_home];

const NavigationService: FC<any> = ({ children }) => {
  const [labelConfigs, setLabelConfigs] = useState<Label[]>([]);

  const fetchNavigation = useCallback(async () => {
    const navigationRes = await getNavigationLabelList(getZipcode());
    if (navigationRes.result) {
      setLabelConfigs(navigationRes.object?.navigate_label_configs ?? []);
    }
  }, []);

  const onNavigationClick = useCallback(
    (navigationKey: NavigateKey) => {
      if (Array.isArray(labelConfigs) && labelConfigs.length) {
        const targetConfig = labelConfigs.filter(item => item.navigate_key === navigationKey)[0];
        if (targetConfig) {
          clickNavigation(targetConfig.navigate_config_id, getZipcode());
          if (!targetConfig.click_appear) {
            setLabelConfigs(labelConfigs.filter(item => item.navigate_key !== navigationKey));
          }
        }
      }
    },
    [labelConfigs]
  );

  usePageMountEffect(() => {
    let currentPageKey = getOldPageKey();
    let isFetch = FETCH_CONFIG_PAGES.some(pageKey => pageKey === currentPageKey);
    if (isFetch) {
      fetchNavigation();
    }
  }, []);

  const apis = useMemo(() => {
    return {
      labelConfigs,
      update: fetchNavigation,
      onClick: onNavigationClick
    };
  }, [labelConfigs, onNavigationClick]);

  return <NavigationContext.Provider value={apis}>{children}</NavigationContext.Provider>;
};

export default NavigationService;
